// src/components/backgroundcheck/BackgroundCheckDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { backgroundCheckAPI } from '../../services/backgroundCheckAPI';
import BackgroundCheckCard from './BackgroundCheckCard';
import BackgroundCheckAssignment from './BackgroundCheckAssignment';
import './style/BackgroundCheckDashboard.css';

const BackgroundCheckDashboard = () => {
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');

  // ✅ Get user role from JWT
  const getUserRole = () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  };

  const userRole = getUserRole();
  
  // ✅ CRITICAL: Only EMPLOYER and ADMIN can manage background checks
  const canManageBackgroundChecks = ['EMPLOYER', 'ADMIN'].includes(userRole);

  const loadBackgroundChecks = useCallback(async () => {
    if (!canManageBackgroundChecks) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (resultFilter) filters.result = resultFilter;
      
      const data = await backgroundCheckAPI.getAll(filters);
      setBackgroundChecks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading background checks:', err);
      setError(err.message || 'Failed to load background checks');
      setBackgroundChecks([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, resultFilter, canManageBackgroundChecks]);

  useEffect(() => {
    loadBackgroundChecks();
  }, [loadBackgroundChecks]);

  const handleAssignSuccess = (message) => {
    setSuccess(message);
    setShowAssignModal(false);
    setSelectedCandidate(null);
    loadBackgroundChecks();
    setTimeout(() => setSuccess(''), 3000);
  };

  const getStatusCounts = () => {
    return {
      total: backgroundChecks.length,
      inProgress: backgroundChecks.filter(bg => bg.status === 'IN_PROGRESS').length,
      completed: backgroundChecks.filter(bg => bg.status === 'COMPLETED').length,
      passed: backgroundChecks.filter(bg => bg.result === 'PASS').length,
      failed: backgroundChecks.filter(bg => bg.result === 'FAIL').length,
    };
  };

  // ✅ Early return for non-employers
  if (!canManageBackgroundChecks) {
    return (
      <div className="background-check-dashboard">
        <div className="access-denied">
          <div className="access-denied-icon">🔒</div>
          <h3>Access Restricted</h3>
          <p>Background check management is only available for employers.</p>
          <p className="role-info">
            Your current role: <strong>{userRole || 'Unknown'}</strong>
          </p>
          <p className="help-text">
            If you are an employer, please contact support to update your account permissions.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="background-check-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading background checks...</p>
        </div>
      </div>
    );
  }

  const counts = getStatusCounts();

  return (
    <div className="background-check-dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Background Check Management</h2>
          <p className="subtitle">Manage and track candidate background verifications</p>
        </div>
        <button
          onClick={() => setShowAssignModal(true)}
          className="btn btn-primary"
        >
          + Assign Background Check
        </button>
      </div>

      {/* Alert Messages */}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {success}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon stat-icon-total">📋</div>
          <div className="stat-content">
            <p className="stat-label">Total Checks</p>
            <p className="stat-value">{counts.total}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-progress">⏳</div>
          <div className="stat-content">
            <p className="stat-label">In Progress</p>
            <p className="stat-value">{counts.inProgress}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-completed">✓</div>
          <div className="stat-content">
            <p className="stat-label">Completed</p>
            <p className="stat-value">{counts.completed}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-passed">✓</div>
          <div className="stat-content">
            <p className="stat-label">Passed</p>
            <p className="stat-value success-text">{counts.passed}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-failed">✗</div>
          <div className="stat-content">
            <p className="stat-label">Failed</p>
            <p className="stat-value error-text">{counts.failed}</p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="statusFilter">Filter by Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="PENDING_CONSENT">Pending Consent</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="ON_HOLD">On Hold</option>
            <option value="UNABLE_TO_VERIFY">Unable to Verify</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="resultFilter">Filter by Result:</label>
          <select
            id="resultFilter"
            value={resultFilter}
            onChange={(e) => setResultFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Results</option>
            <option value="PASS">Pass</option>
            <option value="FAIL">Fail</option>
            <option value="CONDITIONAL">Conditional</option>
          </select>
        </div>

        {(statusFilter || resultFilter) && (
          <button
            onClick={() => {
              setStatusFilter('');
              setResultFilter('');
            }}
            className="btn btn-secondary btn-sm"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Background Checks List */}
      <div className="background-checks-section">
        {backgroundChecks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No Background Checks Found</h3>
            <p>
              {statusFilter || resultFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Get started by assigning a background check to a candidate.'}
            </p>
            {!statusFilter && !resultFilter && (
              <button
                onClick={() => setShowAssignModal(true)}
                className="btn btn-primary"
              >
                Assign Background Check
              </button>
            )}
          </div>
        ) : (
          <div className="background-checks-grid">
            {backgroundChecks.map((bgCheck) => (
              <BackgroundCheckCard
                key={bgCheck.id}
                backgroundCheck={bgCheck}
                onRefresh={loadBackgroundChecks}
              />
            ))}
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignModal && (
        <BackgroundCheckAssignment
          candidate={selectedCandidate}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedCandidate(null);
          }}
          onSuccess={handleAssignSuccess}
        />
      )}
    </div>
  );
};

export default BackgroundCheckDashboard;
